import { Component, Type, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { IonButton, IonIcon, ModalController } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { ClickStopPropagationDirective } from '@okcargo-apps/ng-utils';
import { EStopType, IFreightBasicForCarrier } from '@okcargo-apps/shared-domain';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { FreightCarrierStoreService } from '../freight-carrier-store.service';
import { FreightCarrierStopFinishModalComponent } from '../freight-carrier-stop-finish-modal/freight-carrier-stop-finish-modal.component';
import { FreightCarrierStopStartModalComponent } from '../freight-carrier-stop-start-modal/freight-carrier-stop-start-modal.component';

@Component({
  selector: 'lib-freight-carrier-stop-button',
  templateUrl: './freight-carrier-stop-button.component.html',
  styleUrls: ['./freight-carrier-stop-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonIcon, IonButton, ClickStopPropagationDirective, TranslateModule],
})
export class FreightCarrierStopButtonComponent {
  private readonly modalCtrl = inject(ModalController);
  private readonly freightCarrierStoreService = inject(FreightCarrierStoreService);
  private readonly connectivity = inject(ConnectivityService);

  readonly freight = input.required<IFreightBasicForCarrier>();

  /** Starting/finishing a stop is a command: it cannot be done without connection. */
  protected readonly isOffline = this.connectivity.isOffline;

  protected readonly buttonColor = computed<string>(() => {
    switch (this.freight().nextStopType) {
      case EStopType.LOAD:
        return 'success';
      case EStopType.UNLOAD_LOAD:
        return 'tertiary';
      default:
        return 'primary';
    }
  });

  saveStartStop(): Promise<void> {
    return this.openStopModal(FreightCarrierStopStartModalComponent);
  }

  saveEndStop(): Promise<void> {
    return this.openStopModal(FreightCarrierStopFinishModalComponent);
  }

  private async openStopModal(component: Type<unknown>): Promise<void> {
    // Tells the store to load this freight's detail; same id = cached resource, no refetch.
    // The modal receives the VM signal reference, so it renders the loading state itself.
    this.freightCarrierStoreService.setSelectedFreightId(this.freight().id);

    const modal = await this.modalCtrl.create({
      component,
      componentProps: { freightVM: this.freightCarrierStoreService.freightDetailed },
      backdropDismiss: false,
    });
    await modal.present();

    const { role } = await modal.onDidDismiss();
    if (role === 'confirm') {
      this.freightCarrierStoreService.reloadAfterSaveData();
    }
  }
}
